import { CheckCircleOutlined } from '@ant-design/icons';
import { Tag } from 'antd';
// From 'antd', not 'antd/lib/empty': the CommonJS build is a separate module
// instance with its own React context, so a component imported from it never
// sees our ConfigProvider and silently renders on antd's default theme.
import { Empty } from 'antd';
import IconSquare from 'assets/logos/icon-square.svg?react';
import { Button } from 'components/ui';
import { colors, radius, shadows } from 'design';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PostMetadataDTO } from 'services/api/models/PostDTO';
import { routes } from 'services/RouteService';
import onArticleSelected, { PosthogOnArticleOpenExtras } from 'utils/ContentUtils';

const useStyles = createUseStyles({
  postsContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    marginTop: 16,
    gap: 12,
  },
  post: {
    width: '100%',
    height: 64,
    backgroundColor: 'white',
    // Matches the search bar (`Input`'s `borderRadiusLG`), not its own
    // one-off value, so the two read as the same shape language.
    borderRadius: radius.lg,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    paddingRight: 16,
    boxShadow: shadows.xs,
  },
  coverImageContainer: {
    width: 64,
    height: 64,
    flexShrink: 0,
    overflow: 'hidden',
  },
  coverImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    opacity: 0.7,
  },
  postTitleContainer: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    gap: 8,
    '& .ant-tag': {
      margin: 0,
    },
  },
  postTitleContainerRead: {
    composes: '$postTitleContainer',
    color: colors.text.disabled,
  },
  postTitleAndLabel: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  postLabel: {
    fontSize: '0.8em',
    color: colors.text.secondary,
  },
  postTitle: {
    margin: 0,
    fontWeight: 'normal',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // With a label underneath, two lines of title would make the row too tall
  postTitleWithLabel: {
    composes: '$postTitle',
    '-webkit-line-clamp': 1,
  },
});

export default function PostsList({
  posts,
  limitPosts,
  posthogOnArticleOpenExtras,
  extraReadMarkers,
  getLabel,
  testIdPrefix = 'postinlist',
}: {
  posts: PostMetadataDTO[];
  limitPosts?: number;
  posthogOnArticleOpenExtras?: PosthogOnArticleOpenExtras;
  /** When true, show a "Lu" Tag with check mark for read posts */
  extraReadMarkers?: boolean;
  /** A small line under the title, e.g. the theme a post belongs to */
  getLabel?: (post: PostMetadataDTO) => string | undefined;
  /** Several lists on one page (the categorised Infos page) need distinct test ids */
  testIdPrefix?: string;
}) {
  const styles = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const displayedPosts = limitPosts ? posts.slice(0, limitPosts) : posts;

  const displayShowMore = limitPosts && posts.length > limitPosts;

  return (
    <div className={styles.postsContainer}>
      {posts.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('infoPage.noPosts')} />
      ) : (
        displayedPosts?.map((post, index) => (
          <div
            className={styles.post}
            key={post.id}
            data-testid={`${testIdPrefix}-${index}`}
            onClick={() => onArticleSelected(post, index, navigate, dispatch, posthogOnArticleOpenExtras)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                onArticleSelected(post, index, navigate, dispatch, posthogOnArticleOpenExtras);
              }
            }}
            role="button"
            tabIndex={0}
          >
            <div className={styles.coverImageContainer}>
              {post.cover_image_url ? (
                <img src={post.cover_image_url} alt={post.title} className={styles.coverImage} />
              ) : (
                <div className={styles.coverImage}>
                  <IconSquare className={styles.coverImage} />
                </div>
              )}
            </div>
            <div className={post.read ? styles.postTitleContainerRead : styles.postTitleContainer}>
              <div className={styles.postTitleAndLabel}>
                <h4 className={getLabel?.(post) ? styles.postTitleWithLabel : styles.postTitle}>{post.title}</h4>
                {getLabel?.(post) && <span className={styles.postLabel}>{getLabel(post)}</span>}
              </div>
              {post.is_new && !post.read && <Tag color="success">{t('infoPage.new')}</Tag>}
              {extraReadMarkers && post.read && (
                <Tag color="success" icon={<CheckCircleOutlined />}>
                  {t('infoPage.read')}
                </Tag>
              )}
            </div>
          </div>
        ))
      )}
      {displayShowMore && (
        <Button variant="primary" block size="lg" onClick={() => navigate(routes.info.route)}>
          {t('home.info.showMore')}
        </Button>
      )}
    </div>
  );
}
